import { getAlpha2WithExceptions } from '@/components/Checkout/_helpers/functions/getAlpha2WithExceptions'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { type SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'
import { toAlpha3 } from '@/lib/utils/localization/toAlpha3'
import { appInitialValues } from '@/store/initialValues/appInitialValues'
import { checkoutInitialValues } from '@/store/initialValues/checkoutInitialValues'
import { orderInitialValues } from '@/store/initialValues/orderInitialValues'
import useGlobalStore from '@/store/useGlobalStore'
import { useDeleteCookie } from 'cookies-next/client'
import { useRouter } from 'next/router'
import { useGTM } from '../google-analytics/useGTM'
import useClientRedirect from '../routing/useRedirect'

export const useHandleChangeLocale = () => {
  const { clientRedirect } = useClientRedirect()
  const {
    app: { alpha2, language, translations },
    user: { refID, customer },
    setApp,
    setCheckout,
    setOrder,
    clearCart,
  } = useGlobalStore()

  const { gtm } = useGTM()

  const router = useRouter()
  const deleteCookie = useDeleteCookie()

  const mixPanelTrackLanguage = (
    newLanguage: string,
    previousLanguage: string,
  ): void => {
    if (newLanguage === previousLanguage) return

    deleteCookie('language')
    mixpanelService.registerSuperProperties({
      user_language: newLanguage,
    })

    if (customer?.id.unicity) {
      mixpanelService.identify(customer?.id.unicity)
    }

    mixpanelService
      .setPeopleProperties({
        user_language: newLanguage,
      })
      .catch(() => {})

    mixpanelService
      .trackEvent(MixpanelEvent.USER_LANGUAGE_CHANGED, {
        previous_user_language: previousLanguage,
        user_language: newLanguage,
      })
      .catch(() => {})
  }
  const mixPanelTrackMarket = (
    newCountry: string,
    previousCountry: string,
  ): void => {
    if (previousCountry && newCountry !== previousCountry) {
      mixpanelService.registerSuperProperties({
        user_market: getAlpha2WithExceptions({
          alpha2: newCountry as SupportedAlpha2CountryCode,
        }),
        user_market_extended: newCountry,
      })

      if (customer?.id.unicity) {
        mixpanelService.identify(customer?.id.unicity)
      }

      mixpanelService
        .setPeopleProperties({
          user_market: getAlpha2WithExceptions({ alpha2 }),
          user_market_extended: alpha2,
        })
        .catch(() => {})

      mixpanelService
        .trackEvent(MixpanelEvent.USER_MARKET_CHANGED, {
          previous_user_market: previousCountry,
          event_location: 'home',
        })
        .catch(() => {})
    }
  }

  const handleChangeLocale = async ({
    newAlpha2,
    newLanguage,
    isRedirectToHome = true,
  }: {
    newAlpha2: SupportedAlpha2CountryCode
    newLanguage: SupportedLanguage
    isRedirectToHome?: boolean
  }) => {
    let params = {}
    if (newAlpha2 === alpha2 && newLanguage === language) return
    if (refID) params = { refID }

    await new Promise((resolve) => {
      if (newAlpha2 !== alpha2) {
        gtm.marketChanged({
          newMarket: newAlpha2,
          previous_user_market_extended: alpha2,
        })
        mixPanelTrackMarket(newAlpha2, alpha2)
      }

      gtm.languageChanged({
        new_language: newLanguage,
        previous_user_language: language,
      })
      mixPanelTrackLanguage(newLanguage, language)

      setTimeout(resolve, 500)
    })

    const redirectPath = isRedirectToHome
      ? `/${toAlpha3(newAlpha2)}/${newLanguage}`
      : router.asPath.replace(language, newLanguage)

    await clientRedirect
      .to({
        path: redirectPath,
        withPersistingLocale: false,
        params,
      })
      .catch(() => {})

    setApp({ ...appInitialValues, translations, language: newLanguage })
    setCheckout({ ...checkoutInitialValues })
    setOrder({ ...orderInitialValues })
    clearCart()
  }

  return { handleChangeLocale }
}
